/**
 * ViewPlanningV2.utils.js
 *
 * Helpers behind the View Planning page — response mapping and table locale
 * config kept out of the component tree so the page and drill-down
 * components stay presentational.
 */
import React from "react";
import { Spin, Typography } from "antd";

const { Text } = Typography;

// The table's empty row doubles as its loading state (rows === null): a
// spinner inside the row, under the header. antd's own `loading` overlay
// centers on the flex-stretched box, which can land far below the table.
export const tableEmptyLocale = (rows, emptyMessage) => ({
  emptyText:
    rows === null ? (
      <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin />
      </div>
    ) : (
      <Text type="secondary">{emptyMessage}</Text>
    ),
});

// GET /talent-mobility/jd-active-employees rows → occupant rows ranked by
// SMI (highest first). Display id is the HRIS emp_id, never the unique id —
// userId stays on `id` for availability matching and navigation.
export const mapJdActiveEmployeesToOccupants = (employees) =>
  employees
    .map((employee) => ({
      // null (never coerced to 0) when this occupant hasn't updated their
      // profile against this role yet — "not scored" vs. "scored zero"
      // (see useTalentMobilityData's equivalent apiOccupants mapping).
      // buildScoreColumn's shared SMI/SRI render falls back to a dash +
      // tooltip for a null value.
      matchPct: employee.profile_score == null ? null : Math.round(employee.profile_score),
      person: {
        id: employee.userId,
        employeeId: employee.emp_id,
        name: employee.name,
        nextRoles: null,
      },
    }))
    .sort((a, b) => b.matchPct - a.matchPct);

// The saved plan's availability — who was marked available…
export const getAvailableEmployeeIds = (planEmployees) =>
  new Set((planEmployees || []).map((employee) => employee.employee_unique_id));

// …and which roles each of them was targeted to.
export const buildTargetedRoleIdsByEmployee = (planEmployees) => {
  const byEmployee = new Map();
  (planEmployees || []).forEach((employee) => {
    byEmployee.set(
      employee.employee_unique_id,
      new Set((employee.targeted_roles || []).map((role) => role.jd_sb_unique_id))
    );
  });
  return byEmployee;
};

// …and the Reason for Shortlisting each one was saved with — only
// employees that actually carry a non-blank reason get an entry, so the
// column can render its icon purely on presence.
export const buildShortlistReasonByEmployee = (planEmployees) =>
  new Map(
    (planEmployees || [])
      .filter((employee) => employee.shortlist_reason?.trim())
      .map((employee) => [employee.employee_unique_id, employee.shortlist_reason.trim()])
  );

// The Retained/At Risk cutoff — the plan row's stored target_position when
// it's a positive number, otherwise everyone counts as retained.
export const resolveCutoff = (storedTarget, occupantCount) => {
  const target = Number(storedTarget);
  return Number.isFinite(target) && target > 0 ? target : occupantCount;
};

// GET /view-planning/paic-redesign-plan/roles rows → one list per role tab
// (see PAIC_ROLE_TABS), keyed by tab key. A change_type no tab lists goes to
// the first tab, so every returned role is shown and the tab counts add up.
export const groupPaicRolesByTab = (roles, tabs) => {
  const groups = Object.fromEntries(tabs.map((tab) => [tab.key, []]));
  (roles || []).forEach((role) => {
    const tab = tabs.find((candidate) => candidate.changeTypes.includes(role.change_type)) || tabs[0];
    groups[tab.key].push(role);
  });
  return groups;
};

// A saved PAIC plan's Export CSV (View Planning plans list) — one row per role
// the plan stores (GET /view-planning/paic-redesign-plan/roles), same columns
// as the PAIC version page's own Export CSV.
export const buildPaicPlanRolesCsvRows = (roles) =>
  (roles || []).map((role) => ({
    Role: role.title || role.jd_sb_unique_id || role.role_key,
    "Change Type": role.change_type || "",
    "Before HC": role.before_hc ?? "",
    "After HC (Approved)": role.after_hc ?? "",
    "Shortlisted Employees": role.shortlisted_count ?? 0,
  }));
